import React, {
    ForwardedRef,
    forwardRef,
    ReactElement,
    ReactNode,
    Ref,
    RefAttributes,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';

import { useCloseDropdownOnClickOutside } from '../../hooks/useCloseDropdownOnClickOutside';

import styles from './Dropdown.module.css';

const POSITION_CLASSES = {
    'bottom-left': styles.positionBottomLeft,
    'bottom-right': styles.positionBottomRight,
    'top-right': styles.positionTopRight,
    'top-center': styles.positionTopCenter,
} as const;

type DropdownContentPosition = keyof typeof POSITION_CLASSES;

export type DropdownRef = {
    close: () => void;
};

type DropdownRole = 'menu' | 'listbox' | 'dialog';

export type RenderDropdownTrigger<T extends HTMLElement = HTMLElement> = (props: {
    onClick: () => void;
    ref: Ref<T>;
    isActive: boolean;
    'aria-expanded': boolean;
    'aria-controls'?: string;
    id?: string;
}) => ReactElement;

interface DropdownProps<T extends HTMLElement = HTMLElement> {
    content: ReactNode;
    renderDropdownTrigger: RenderDropdownTrigger<T>;
    position?: DropdownContentPosition;
    className?: string;
    contentClassName?: string;
    id?: string;
    role?: DropdownRole;
}
function _Dropdown<T extends HTMLElement>(
    {
        content,
        position = 'bottom-left',
        renderDropdownTrigger,
        className,
        contentClassName,
        id,
        role,
    }: DropdownProps<T>,
    ref: ForwardedRef<DropdownRef>
) {
    const elementRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<T>(null);
    const [isOpen, setIsOpen] = useState(false);
    useCloseDropdownOnClickOutside(elementRef, openerRef, setIsOpen);
    const handleToggle = () => {
        setIsOpen((prev) => !prev);
    };
    const handleClose = () => setIsOpen(false);
    useImperativeHandle(
        ref,
        () => ({
            close: handleClose,
        }),
        []
    );
    const contentId = id ? `${id}-content` : undefined;
    const triggerId = id ? `${id}-trigger` : undefined;
    return (
        <div className={classnames(styles.root, className)}>
            {renderDropdownTrigger({
                onClick: handleToggle,
                ref: openerRef,
                isActive: isOpen,
                'aria-expanded': isOpen,
                'aria-controls': contentId,
                id: triggerId,
            })}
            {isOpen && (
                <div
                    className={classnames(
                        styles.content,
                        POSITION_CLASSES[position],
                        contentClassName
                    )}
                    ref={elementRef}
                    role={role}
                    aria-labelledby={triggerId ?? undefined}
                    id={contentId}
                >
                    {content}
                </div>
            )}
        </div>
    );
}
export const Dropdown = forwardRef(_Dropdown) as (<T extends HTMLElement = HTMLElement>(
    props: DropdownProps<T> & RefAttributes<DropdownRef>
) => ReactElement | null) & { displayName?: string };

Dropdown.displayName = 'Dropdown';
